
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { 
  deleteClient, 
  toggleClientActiveStatus,
  addClient as addClientService,
  updateClientInfo
} from '@/services/admin';
import { useClientInvitations } from './useClientInvitations';

/**
 * Hook for client CRUD operations
 */
export const useClientCrud = (
  onboardingRequests: any[],
  setOnboardingRequests: React.Dispatch<React.SetStateAction<any[]>>,
  refreshRequests: () => Promise<void>
) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isAddClientOpen, setIsAddClientOpen] = useState<boolean>(false);
  const [isEditClientOpen, setIsEditClientOpen] = useState<boolean>(false);
  const [clientToEdit, setClientToEdit] = useState<any>(null);

  // Use invitation management hook
  const { 
    isProcessing: invitationProcessing, 
    handleSendInvitation,
    handleSendBulkInvitations
  } = useClientInvitations(onboardingRequests, setOnboardingRequests);

  console.log('useClientCrud hook state:', {
    isEditClientOpen,
    clientToEdit: clientToEdit?.id || 'none',
    isProcessing: isProcessing || invitationProcessing
  });

  const handleDeleteClient = async (client: any) => {
    if (window.confirm(`Are you sure you want to delete ${client.full_name || client.email}? This action cannot be undone.`)) {
      setIsProcessing(true);
      
      try {
        const { success, error } = await deleteClient(client.id);
        
        if (!success) throw error;
        
        // Update the local state by removing the deleted client
        setOnboardingRequests((prevRequests) => 
          prevRequests.filter(req => req.id !== client.id)
        );
        
        toast({
          title: "Client Deleted",
          description: `${client.full_name || client.email} has been successfully deleted.`,
        });
        
      } catch (error: any) {
        console.error('Error deleting client:', error);
        toast({
          title: "Error",
          description: `Failed to delete client: ${error.message || 'Unknown error'}`,
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleToggleActive = async (client: any) => {
    setIsProcessing(true);
    
    try {
      const newActiveState = !client.is_active;
      const { success, updatedRecord, error } = await toggleClientActiveStatus(client.id, newActiveState);
      
      if (!success) throw error;
      
      // Update local state to reflect the change
      setOnboardingRequests((prevRequests) => 
        prevRequests.map(req => 
          req.id === client.id ? { ...req, is_active: newActiveState } : req
        )
      );
      
      toast({
        title: newActiveState ? "Client Activated" : "Client Deactivated",
        description: `${client.full_name || client.email} has been ${newActiveState ? 'activated' : 'deactivated'}.`,
      });
      
    } catch (error: any) {
      console.error('Error toggling client active status:', error);
      toast({
        title: "Error",
        description: `Failed to update client status: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddClient = async (clientData: any) => {
    setIsProcessing(true);
    
    try {
      const { success, client, error } = await addClientService(clientData);
      
      if (!success) throw error;
      
      // Update local state with the new client
      setOnboardingRequests((prev) => [client, ...prev]);
      
      // Close the dialog
      setIsAddClientOpen(false);
      
      toast({
        title: "Client Added",
        description: `${clientData.fullName} has been successfully added.`,
      });
      
      // Refresh the list
      await refreshRequests();
      
    } catch (error: any) {
      console.error('Error adding client:', error);
      toast({
        title: "Error",
        description: `Failed to add client: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditClient = (client: any) => {
    console.log('=== handleEditClient called ===');
    console.log('Received client:', client);
    console.log('Current state before update:', {
      isEditClientOpen,
      clientToEdit: clientToEdit?.id || 'none'
    });
    
    try {
      console.log('Setting clientToEdit to:', client);
      setClientToEdit(client);
      
      console.log('Setting isEditClientOpen to true');
      setIsEditClientOpen(true);
      
      console.log('Edit client dialog should now be open');
    } catch (error) {
      console.error('Error in handleEditClient:', error);
    }
  };

  const updateClient = async (clientData: any) => {
    console.log('=== updateClient called ===');
    console.log('Client data to update:', clientData);
    
    setIsProcessing(true);
    
    try {
      const { success, updatedClient, error } = await updateClientInfo(clientData);
      
      if (!success) throw error;
      
      // Update the local state with the updated client information
      setOnboardingRequests((prevRequests) => 
        prevRequests.map(req => 
          req.id === clientData.id ? { ...req, 
            full_name: clientData.fullName,
            email: clientData.email,
            company_name: clientData.companyName || null,
            phone: clientData.phone || null
          } : req
        )
      );
      
      // Close the dialog
      setIsEditClientOpen(false);
      
      toast({
        title: "Client Updated",
        description: `${clientData.fullName}'s information has been updated successfully.`,
      });
      
    } catch (error: any) {
      console.error('Error updating client information:', error);
      toast({
        title: "Error",
        description: `Failed to update client information: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isAddClientOpen,
    setIsAddClientOpen,
    isEditClientOpen,
    setIsEditClientOpen,
    clientToEdit,
    isProcessing: isProcessing || invitationProcessing,
    handleDeleteClient,
    handleToggleActive,
    handleAddClient,
    handleEditClient,
    updateClient,
    handleSendInvitation,
    handleSendBulkInvitations
  };
};
